import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api, unwrap, unwrapPage } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components, operations } from '@/api/schema';

export type ClaseItem = components['schemas']['ClassItem'];
export type Clase = components['schemas']['ClassDetail'];
export type PreguntaClase = components['schemas']['ClassQuestionBody'];
export type GrupoRecursos = 'english' | 'plus' | 'guides';
export type FiltrosClases = NonNullable<operations['getAdminClasses']['parameters']['query']>;

const clave = (id: number) => ['contenido', 'clase', id];

export function useClases(query: FiltrosClases) {
  return useQuery({
    queryKey: ['contenido', 'clases', query],
    queryFn: () => unwrapPage<ClaseItem[]>(api.GET('/api/v1/admin/classes', { params: { query } })),
    placeholderData: keepPreviousData,
  });
}

export function useClase(id: number) {
  return useQuery({
    queryKey: clave(id),
    queryFn: () =>
      unwrap<Clase>(
        api.GET('/api/v1/admin/classes/{id}', { params: { path: { id: String(id) } } }),
      ),
  });
}

/**
 * Todas las mutaciones de la clase devuelven el detalle completo, que reemplaza el de la caché.
 * `exito` es el mensaje que se muestra al terminar.
 */
export function useClaseMutacion<V>(id: number, fn: (v: V) => Promise<Clase>, exito?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (d) => {
      qc.setQueryData(clave(id), d);
      void qc.invalidateQueries({ queryKey: ['contenido', 'clases'] });
      if (exito) toast.success(exito);
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
}

export const rutaClase = (id: number) => ({ params: { path: { id: String(id) } } });
export const rutaHijo = (id: number, childId: number) => ({
  params: { path: { id: String(id), childId: String(childId) } },
});
export const rutaGrupo = (id: number, group: GrupoRecursos) => ({
  params: { path: { id: String(id), group } },
});
